import { useEffect, useMemo, useState } from "react"
import { Column, Data, EditableFormField, EditablePanel, FieldType, FieldValue, ModalId, Section, Table, UIPermission, useUIActions } from "../../../../../features/ui"
import { TableName } from "../../../../../tables"
import { useSystemStateHooks } from "../../../../system"
import { usePurchasesHooks } from "../../../../purchases"
import { useProductLoaders } from "../../../loaders/productLoaders"
import { Product } from "../../../state/types"
import { NegativeStockCategoryFormModal } from "../negative-stock-category-form-modal/negativeStockCategoryFormModal"
import { NegativeStockProductsFormModal } from "../negative-stock-products-form-modal/negativeStockProductsFormModal"
import { useActions } from "./actions"

// Configuración de productos → Venta Sin Stock: habilitación masiva por categoría (regla) o por producto
export const NegativeStockPanel = () => {
    const { userHasPermission } = useSystemStateHooks()
    const readOnlyCategories = !userHasPermission(UIPermission.SAVE_PURCHASES_SETTINGS)
    const readOnlyProducts = !userHasPermission(UIPermission.SAVE_PRODUCTS)
    const { getNegativeStockCategoryIds } = usePurchasesHooks()
    const enabledCategoryIds = getNegativeStockCategoryIds()

    const [products, setProducts] = useState<Product[]>([])

    const productLoaders = useProductLoaders()
    const { submitAddCategory, submitRemoveCategory, submitEnableProducts, submitDisableProduct } = useActions()
    const { openModal } = useUIActions()

    const loadProducts = async () => {
        const { loadAllProducts } = productLoaders()
        setProducts(await loadAllProducts())
    }

    useEffect(() => {
        loadProducts()
    }, [])

    const categoryRows: Data[] = useMemo(() => enabledCategoryIds.map(categoryId => ({
        categoryId,
        productsCount: products.filter(product => product.categoryId === categoryId && !product.loted && !product.archived).length
    })), [enabledCategoryIds, products])

    const productRows: Data[] = useMemo(() => products
        .filter(product => product.allowNegativeStock && !product.loted && !product.archived)
        .sort((product_A, product_B) => product_A.name.localeCompare(product_B.name, 'es', { numeric: true, sensitivity: 'base' }))
        .map(product => ({
            productId: product.id,
            code: product.code,
            name: product.name,
            categoryId: product.categoryId
        }))
    , [products])

    const categoryColumns: Column[] = [
        {
            name: 'categoryId',
            type: FieldType.TABLE,
            table: TableName.CATEGORIES,
            label: 'Categoría'
        },
        {
            name: 'productsCount',
            type: FieldType.NUMBER,
            label: 'Productos sin Partida'
        }
    ]

    const productColumns: Column[] = [
        {
            name: 'code',
            type: FieldType.NUMBER,
            label: '#',
            render: (value?: FieldValue) => <>#{value}</>
        },
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Producto'
        },
        {
            name: 'categoryId',
            type: FieldType.TABLE,
            table: TableName.CATEGORIES,
            label: 'Categoría'
        }
    ]

    const onAddCategory = () => openModal(ModalId.NEGATIVE_STOCK_CATEGORY_FORM)

    const onAddProducts = () => openModal(ModalId.NEGATIVE_STOCK_PRODUCTS_FORM)

    const onEnableProducts = async (selectedProducts: Product[]) => {
        await submitEnableProducts(selectedProducts)
        await loadProducts()
    }

    const onRemoveProduct = async (productId: string) => {
        const product = products.find(product => product.id === productId)
        if (!product) return
        await submitDisableProduct(product)
        await loadProducts()
    }

    const fields: EditableFormField[] = [
        {
            name: 'negativeStockCategories',
            type: FieldType.TABLE,
            table: TableName.CATEGORIES,
            label: 'Habilitar por Categoría',
            hint: 'Incluye productos que se agreguen a futuro en la categoría.',
            disabled: true,
            render: () => (
                <Section id="negativeStockCategories" space={12}>
                    <Table
                        columns={categoryColumns}
                        rows={categoryRows}
                        rowId="categoryId"
                        readOnly={readOnlyCategories}
                        createIcon="plus"
                        small
                        onCreate={onAddCategory}
                        onRemove={submitRemoveCategory}
                    />
                </Section>
            )
        },
        {
            name: 'negativeStockProducts',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            label: 'Habilitar por Producto',
            hint: 'Los productos con partida no se pueden habilitar.',
            disabled: true,
            render: () => (
                <Section id="negativeStockProducts" space={12}>
                    <Table
                        columns={productColumns}
                        rows={productRows}
                        rowId="productId"
                        readOnly={readOnlyProducts}
                        createIcon="plus"
                        small
                        onCreate={onAddProducts}
                        onRemove={onRemoveProduct}
                    />
                </Section>
            )
        }
    ]

    return (
        <>
            <EditablePanel
                fields={fields}
                readOnly={readOnlyCategories && readOnlyProducts}
            />
            <NegativeStockCategoryFormModal
                enabledCategoryIds={enabledCategoryIds}
                onSubmit={submitAddCategory}
            />
            <NegativeStockProductsFormModal
                products={products}
                enabledCategoryIds={enabledCategoryIds}
                onSubmit={onEnableProducts}
            />
        </>
    )
}
